<!-- Group by: Roaster · Flavour · Brew · Origin. Changing it re-racks the wall.
     A radio group of sage pill chips (the dock's first row); the chosen one is ink. -->
<template>
  <div class="group-bar" :class="`group-bar--${tone}`" role="radiogroup" aria-label="Group by">
    <span class="group-bar__label" aria-hidden="true">Group by</span>
    <button
      v-for="g in GROUPINGS"
      :key="g.id"
      type="button"
      role="radio"
      class="chip"
      :class="{ 'chip--on': grouping === g.id }"
      :aria-checked="grouping === g.id"
      @click="choose(g.id)"
    >
      {{ g.label }}
    </button>
  </div>
</template>

<script setup>
import { GROUPINGS, grouping } from '../store.js';
import { haptic } from '../core/util.js';

defineProps({ tone: { type: String, default: 'dock' } });

function choose (id) {
  if (grouping.value === id) return;
  haptic('tick');
  grouping.value = id;
}
</script>

<style scoped>
/* The chips share the row (flex 1 1 0), so they always fit and never scroll.
   Should a narrow screen or large text ever overflow it, the row is a native
   horizontal scroller, never a clipped one. */
.group-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  /* Not pan-x alone: in the list view this row sits in a vertical scroller,
     and a finger starting on a chip must still scroll the list. */
  touch-action: pan-x pan-y;
  padding: 2px;
  margin: -2px;
}
.group-bar::-webkit-scrollbar { display: none; }
.group-bar__label {
  position: absolute;
  width: 1px; height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
}
.chip {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  /* A 44px box; the 40px pill is its padding box (a 2px transparent border,
     background clipped to the padding), and the -2px margin gives the border
     back, so every pill is exactly the size and place it was. The border goes
     all round, not just top and bottom, or the pill's end caps turn elliptical. */
  height: 44px;
  margin: -2px;
  padding: 0 6px;
  border: 2px solid transparent;
  background-clip: padding-box;
  border-radius: var(--radius-pill, 999px);
  background-color: var(--sage, #E6EDE4);
  color: var(--ink-2, #3E4A41);
  font-family: var(--font-body, 'Manrope', sans-serif);
  font-size: var(--text-sm);
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: background-color 180ms ease-out, color 180ms ease-out, transform 140ms ease-out;
}
.chip:active { transform: scale(0.96); }
.chip--on { background-color: var(--ink, #1F2A22); color: var(--dock, #FAF7F1); }
.chip:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 0; }

@media (prefers-reduced-motion: reduce) {
  .chip { transition: none; }
}
</style>
